import React from 'react';
import { Card, Tabs, Typography } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { SalesAnalyticsTab } from '@/services/salesAnalytics.service';
import { SalesAnalyticsFilterBar, useSalesAnalyticsFilters } from './SalesAnalyticsFilterBar';
import { SalesAnalyticsOverviewTab } from './SalesAnalyticsOverviewTab';
import { SalesAnalyticsPresalesTab } from './SalesAnalyticsPresalesTab';
import { SalesAnalyticsOrdersTab } from './SalesAnalyticsOrdersTab';
import { SalesAnalyticsInvoicingTab } from './SalesAnalyticsInvoicingTab';
import { SalesAnalyticsCollectionsTab } from './SalesAnalyticsCollectionsTab';
import { SalesAnalyticsCustomersTab } from './SalesAnalyticsCustomersTab';
import { SalesAnalyticsTeamTab } from './SalesAnalyticsTeamTab';
import { useAuthStore } from '@/store/authStore';
import { getRefreshIntervalMs } from '@/components/sales/control-tower/layoutProfile';

const { Paragraph, Text } = Typography;

const TAB_ITEMS: { key: SalesAnalyticsTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'presales', label: 'Presales' },
  { key: 'orders', label: 'Orders & Fulfillment' },
  { key: 'invoicing', label: 'Invoicing & AR' },
  { key: 'collections', label: 'Collections' },
  { key: 'customers', label: 'Customers' },
  { key: 'team', label: 'Team' },
];

export const SalesAnalyticsWorkspace: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, filterSummary } = useSalesAnalyticsFilters();
  const analyticsTab = (searchParams.get('analytics_tab') as SalesAnalyticsTab) || 'overview';
  const user = useAuthStore((s) => s.user);
  const refreshMs = getRefreshIntervalMs(user);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!refreshMs) return;
    const id = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['sales-analytics'] });
    }, refreshMs);
    return () => window.clearInterval(id);
  }, [refreshMs, queryClient]);

  const setAnalyticsTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('analytics_tab', key);
    setSearchParams(next, { replace: true });
  };

  const funnelHint =
    filters.funnel_mode === 'cohort'
      ? 'Cohort mode traces leads created in the period through to receipt via document lineage.'
      : 'Volume mode counts documents created or posted in the selected date range at each funnel stage.';

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Period analytics with shared filters. {funnelHint}
        {refreshMs ? (
          <>
            {' '}
            <Text type="secondary">Auto-refresh every {refreshMs / 60000}m.</Text>
          </>
        ) : null}
      </Paragraph>
      <Card size="small" style={{ marginBottom: 16 }}>
        <SalesAnalyticsFilterBar />
        <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
          {filterSummary}
        </Paragraph>
      </Card>

      <Tabs
        activeKey={analyticsTab}
        onChange={setAnalyticsTab}
        items={TAB_ITEMS.map((t) => ({
          key: t.key,
          label: t.label,
          children: (
            <Card size="small">
              {t.key === 'overview' && <SalesAnalyticsOverviewTab filters={filters} filterSummary={filterSummary} />}
              {t.key === 'presales' && <SalesAnalyticsPresalesTab filters={filters} filterSummary={filterSummary} />}
              {t.key === 'orders' && <SalesAnalyticsOrdersTab filters={filters} filterSummary={filterSummary} />}
              {t.key === 'invoicing' && <SalesAnalyticsInvoicingTab filters={filters} filterSummary={filterSummary} />}
              {t.key === 'collections' && <SalesAnalyticsCollectionsTab filters={filters} filterSummary={filterSummary} />}
              {t.key === 'customers' && <SalesAnalyticsCustomersTab filters={filters} filterSummary={filterSummary} />}
              {t.key === 'team' && <SalesAnalyticsTeamTab filters={filters} filterSummary={filterSummary} />}
            </Card>
          ),
        }))}
      />
    </div>
  );
};
